<template>
  <nav
    class="skip-navigation-container"
    role="navigation"
    aria-label="Skip navigation links"
  >
    <a
      v-for="link in skipLinks"
      :key="link.target"
      :href="`#${link.target}`"
      class="skip-navigation"
      :aria-label="link.label"
      @click="handleSkipLink(link.target, $event)"
    >
      {{ link.text }}
    </a>
  </nav>
</template>

<script>
export default {
    name: 'SkipNavigation',

    data() {
        return {
            skipLinks: [
                {
                    target: 'main-content',
                    text: 'Skip to main content',
                    label: 'Skip to main content area'
                },
                {
                    target: 'main-navigation',
                    text: 'Skip to navigation',
                    label: 'Skip to main navigation menu'
                },
                {
                    target: 'search',
                    text: 'Skip to search',
                    label: 'Skip to search functionality'
                },
                {
                    target: 'footer',
                    text: 'Skip to footer',
                    label: 'Skip to page footer'
                }
            ]
        };
    },

    methods: {
        /**
         * Handle skip link activation
         * @param {string} targetId - ID of target element
         * @param {Event} event - Click event
         */
        handleSkipLink(targetId, event) {
            event.preventDefault();

            const targetElement = document.getElementById(targetId);

            if (targetElement) {
                // Make target focusable if it isn't already
                if (!this.isFocusable(targetElement)) {
                    targetElement.setAttribute('tabindex', '-1');
                }

                // Focus the target element
                targetElement.focus();

                // Scroll to target if needed
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                // Announce the skip action to screen readers
                this.announceSkip(targetId);
            } else {
                console.warn(`Skip navigation target not found: ${targetId}`);
            }
        },

        /**
         * Check if element is focusable
         * @param {Element} element
         * @returns {boolean}
         */
        isFocusable(element) {
            const focusableElements = [
                'a[href]',
                'button:not([disabled])',
                'input:not([disabled])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                '[tabindex]:not([tabindex="-1"])',
                '[contenteditable]'
            ];

            return focusableElements.some(selector => element.matches(selector)) ||
                   element.getAttribute('tabindex') === '0';
        },

        /**
         * Announce skip action to screen readers
         * @param {string} targetId
         */
        announceSkip(targetId) {
            const announcer = document.createElement('div');
            announcer.setAttribute('aria-live', 'polite');
            announcer.setAttribute('aria-atomic', 'true');
            announcer.className = 'sr-only';

            const targetName = this.getTargetName(targetId);
            announcer.textContent = `Skipped to ${targetName}`;

            document.body.appendChild(announcer);

            // Remove after announcement
            setTimeout(() => {
                if (document.body.contains(announcer)) {
                    document.body.removeChild(announcer);
                }
            }, 1000);
        },

        /**
         * Get human-readable name for target
         * @param {string} targetId
         * @returns {string}
         */
        getTargetName(targetId) {
            const nameMap = {
                'main-content': 'main content',
                'main-navigation': 'navigation menu',
                'search': 'search',
                'footer': 'page footer'
            };

            return nameMap[targetId] || targetId.replace('-', ' ');
        }
    }
};
</script>

<style scoped>
.skip-navigation-container {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 9999;
}

.skip-navigation {
    position: absolute;
    top: -40px;
    left: 6px;
    background: var(--v-accent-base, #96b3eb);
    color: var(--v-secondary-base, #050505);
    padding: 8px 16px;
    text-decoration: none;
    border-radius: 4px;
    font-weight: bold;
    font-size: 14px;
    line-height: 1.4;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transition: top 0.3s ease, background-color 0.2s ease;
    z-index: 10000;
}

.skip-navigation:focus {
    top: 6px;
    outline: 3px solid #ffffff;
    outline-offset: 2px;
}

.skip-navigation:hover {
    background: var(--v-accent-lighten1, #a6c3eb);
    text-decoration: underline;
}

/* Multiple skip links positioning */
.skip-navigation:nth-child(2) {
    left: 150px;
}

.skip-navigation:nth-child(3) {
    left: 300px;
}

.skip-navigation:nth-child(4) {
    left: 450px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .skip-navigation {
        left: 3px;
        padding: 6px 12px;
        font-size: 13px;
    }

    .skip-navigation:nth-child(2) {
        left: 120px;
    }

    .skip-navigation:nth-child(3) {
        left: 240px;
    }

    .skip-navigation:nth-child(4) {
        left: 360px;
    }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
    .skip-navigation {
        background: #ffffff !important;
        color: #000000 !important;
        border: 2px solid #000000 !important;
    }

    .skip-navigation:focus {
        outline: 3px solid #ffffff !important;
        outline-offset: 3px !important;
    }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
    .skip-navigation {
        transition: none;
    }
}
</style>
